import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { qk } from '@/lib/queryKeys';
import { useTenantId } from './useTenant';

/**
 * Periodinių (pasikartojančių) užsakymų šablonai — the standing jobs a
 * carrier repeats, and the one action that puts today's on the road.
 *
 * `due_today` is computed by the SERVER and rendered as given. It depends on
 * what day it is and on whether the template has already generated for that
 * day, and re-deriving it here would give the console its own opinion about
 * a question the generate route decides — the two would disagree at exactly
 * the moment it mattered, which is a second click at 07:00.
 */
export function useOrderTemplates({ includeArchived = false, date } = {}) {
  const tenantId = useTenantId();
  const params = new URLSearchParams();
  if (includeArchived) params.set('include_archived', '1');
  if (date) params.set('date', date);
  const query = params.toString();

  return useQuery({
    queryKey: qk.orderTemplates(tenantId, { includeArchived, date: date ?? 'today' }),
    queryFn: () => api(`/api/order-templates${query ? `?${query}` : ''}`),
    enabled: Boolean(tenantId),
    // Templates change when somebody edits one, and `due_today` changes when
    // somebody generates — both of which invalidate below. Nothing else moves
    // it inside a working morning.
    staleTime: 60_000,
  });
}

/**
 * Every template mutation shares one invalidation policy, and it reaches
 * further than the template list: generating creates real orders, so the
 * orders table, the map's active set and the dashboard counters are all
 * stale the moment it succeeds. Cheaper than it looks — React Query only
 * refetches queries with a mounted observer.
 */
function useTemplateInvalidation() {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['order-templates', tenantId] });
    queryClient.invalidateQueries({ queryKey: ['orders', tenantId] });
    queryClient.invalidateQueries({ queryKey: qk.dashboard(tenantId) });
  };
}

export function useSaveOrderTemplate() {
  const invalidate = useTemplateInvalidation();
  return useMutation({
    mutationFn: ({ templateId, ...body }) =>
      templateId
        ? api(`/api/order-templates/${templateId}`, { method: 'PUT', body })
        : api('/api/order-templates', { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useArchiveOrderTemplate() {
  const invalidate = useTemplateInvalidation();
  return useMutation({
    mutationFn: (templateId) => api(`/api/order-templates/${templateId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** One standing job's loads for one day. `force` is the deliberate second
 *  run — the server refuses a repeat without it. */
export function useGenerateFromTemplate() {
  const invalidate = useTemplateInvalidation();
  return useMutation({
    mutationFn: ({ templateId, force = false, date }) =>
      api(`/api/order-templates/${templateId}/generate`, {
        method: 'POST',
        body: { force, ...(date ? { date } : {}) },
      }),
    onSuccess: invalidate,
  });
}

/**
 * The morning, in one action. Answers with a per-template result rather than
 * a count, because the route deliberately does NOT stop on the first failure
 * — see generate_all_due() — and "eleven clients went out, this one did not
 * and here is why" is the only useful thing to render.
 */
export function useGenerateDueTemplates() {
  const invalidate = useTemplateInvalidation();
  return useMutation({
    mutationFn: () => api('/api/order-templates/generate-due', { method: 'POST', body: {} }),
    onSuccess: invalidate,
  });
}
